
import { defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { EventBus } from "../../eventBus";
import { IHeatmapConfig } from "@/models/heatmap";
import { mapGetters, mapState } from "vuex";
import { loadHeatmapImg } from "./heatmaps-utils";
import { RootGetters } from "@/store/getters";
import { inputElements } from "@/core/utils";
import IbIcons from "@/components/icons/ib-icons.vue";

declare var require: any;

export default defineComponent({
  setup() {
    const { t, te } = useI18n({
      inheritLocale: true,
      useScope: "global",
    });
    return { t, te };
  },
  data() {
    return {
      active: false,
      currentlyHoveredHeatmap: '',
    };
  },
  components: {
    IbIcons,
  },
  computed: {
    ...mapGetters({
      heatmapsConfig: RootGetters.GET_HEATMAPS_CONFIG,
    }),
    ...mapState(["activeHeatmaps"]),
    state() {
      return {
        activeHeatmaps: this.$store.state.activeHeatmaps,
        isHeatmapsVisible: this.$store.state.isVisible,
        currentSlide: this.$store.state.currentSlide,
        heatmapsOpacity: this.$store.state.heatmapsOpacity,
      };
    },
    heatmapsOpacity: {
      get() {
        return this.$store.state.heatmapsOpacity;
      },
      set(value: number) {
        this.$store.commit("updateHeatmapsOpacity", value);
      },
    },
  },
  mounted() {
    let vm = this;
    EventBus.on("hide-active-heatmaps", this.hideActiveHeatmaps);
    // this event is mainly a logical transfer from the reports.js heamap toggle logic
    // todo: refactor after reports module is implemented in Vue
    EventBus.on("show-heatmap-if-exists", this.showHeatmapIfExists);
    EventBus.on("toggle-heatmap-by-index", this.toggleHeatmapByIndex);
    EventBus.on("apply-heatmap-state", this.applyHeatmapState);
    window.addEventListener("keydown", this.heatmapKeyListener, false);
  },
  unmounted() {
    window.removeEventListener("keydown", this.heatmapKeyListener, false);
    // event bus listeners remove
    EventBus.off("show-heatmap-if-exists", this.showHeatmapIfExists);
    EventBus.off("toggle-heatmap-by-index", this.toggleHeatmapByIndex);
    EventBus.off("apply-heatmap-state", this.applyHeatmapState);
  },
  watch: {
    activeHeatmaps(newVal, oldVal) {
      const activeHeatmap = newVal.find((hm) => hm.isActive);
      if (activeHeatmap) {
        loadHeatmapImg(activeHeatmap.path);
      }
    },
  },
  methods: {
    isDescriptionExist(classification) {
      return this.te(
        `config.heatmap.${this.state.currentSlide.tissueType}.${this.state.currentSlide.stain}.${classification}.description`
      );
    },
    isHeatmapEnabledForSlide(heatmap: any) {
      let isEnabled = this.state.currentSlide.heatmaps.some((hm: any) => {
        return hm.classification === heatmap.classification;
      });
      return isEnabled;
    },
    isHeatmapActive(heatmap: any) {
      return (
        this.state.isHeatmapsVisible &&
        this.state.activeHeatmaps.some(
          (hm: any) => hm.classification === heatmap.classification && hm.isActive
        )
      );
    },
    toggleHeatmap(heatmap: any) {
      if (this.isHeatmapEnabledForSlide(heatmap)) {
        this.$store.commit("toggleHeatmap", heatmap);
        this.$store.commit("updateHeatmapsOpacity", heatmap.opacity);
        EventBus.emit("heatmapSelected", heatmap);
      }
    },
    getCssClass(heatmap: any) {
      return {
        disabled: !this.isHeatmapEnabledForSlide(heatmap),
        active: this.isHeatmapActive(heatmap),
        primary: heatmap.display === "primary",
        secondary: heatmap.display === "secondary",
        hidden: heatmap.display === "hidden",
      };
    },
    getInfoIconColor(heatmap) {
      if (this.currentlyHoveredHeatmap===heatmap.classification && this.isHeatmapActive(heatmap)) {
        return "white";
      }
      return this.getCssClass(heatmap).disabled ? "oof-bg" : "primary";
    },
    heatmapKeyListener(e: { key: string; target: any }) {
      if (inputElements.includes(e.target.tagName.toLowerCase())) {
        return;
      }
      switch (e.key) {
        case "H":
        case "h":
          this.active = !this.active;
          break;
        case " ":
          this.$store.commit("toggleActiveHeatmaps");
          break;
      }
    },
    // event bus functions
    hideActiveHeatmaps() {
      this.$store.commit("hideActiveHeatmaps");
    },
    showHeatmapIfExists(data: { heatmaps: string[]; isToggle: boolean }) {
      let heatmapConfig = null;
      for (let i = 0; i < data.heatmaps.length; i++) {
        heatmapConfig = this.heatmapsConfig.find(
          (config: IHeatmapConfig) => config.classification === data.heatmaps[i]
        );
        if (heatmapConfig && this.isHeatmapEnabledForSlide(heatmapConfig)) {
          if (data.isToggle || !this.isHeatmapActive(heatmapConfig)) {
            this.$store.commit("toggleHeatmap", heatmapConfig);
          }
          break;
        }
      }
      if (!heatmapConfig || !this.isHeatmapEnabledForSlide(heatmapConfig)) {
        this.$store.commit("clearAllHeatmaps");
      }
    },
    toggleHeatmapByIndex(data: { index: number }) {
      const heatmapConfig: IHeatmapConfig = this.heatmapsConfig[data.index];
      if (heatmapConfig && this.isHeatmapEnabledForSlide(heatmapConfig)) {
        this.$store.commit("toggleHeatmap", heatmapConfig);
      }
    },
    applyHeatmapState(data: { opacity?: number; heatmap: string }) {
      const heatmapConfig = this.heatmapsConfig.find(
        (config: IHeatmapConfig) => config.classification === data.heatmap
      );
      if (
        heatmapConfig &&
        this.isHeatmapEnabledForSlide(heatmapConfig) &&
        !this.isHeatmapActive(heatmapConfig)
      ) {
        this.$store.commit("toggleHeatmap", heatmapConfig);
        if (data.opacity) {
          this.$store.commit("updateHeatmapsOpacity", data.opacity);
        }
      }
    },
  },
});
